import React from 'react'
import { useNavigate } from 'react-router-dom'

import { AlertModal, ConfirmModal, Toasts, useModalContext } from '@fourthwall/components'
import { OffersApiClient } from '@fourthwall/services/dist/OffersApiClient'
import type { Offer } from '@fourthwall/services/dist/models/offer'
import { decodeHtmlEntities } from '@fourthwall/utils/dist/sanitize'
import { useDeleteOfferMutation } from '@mutations'
import { useQueryClient } from '@tanstack/react-query'
import { routing } from '@utils/routing'

import { useBundleOfferArchivedMutation } from '../../hooks/useBundleOfferArchivedMutation'
import { useCombinedOfferArchivedMutation } from '../../hooks/useCombinedOfferArchivedMutation'
import { CannotArchiveProductModalContent } from '../components/CannotArchiveProductModalContent/CannotArchiveProductModalContent'

export const useArchiveProduct = () => {
  const { open, close } = useModalContext()
  const { archiveOfferBundleMutation } = useBundleOfferArchivedMutation()
  const { deleteOfferCombinedMutation } = useCombinedOfferArchivedMutation()
  const { deleteOfferMutation } = useDeleteOfferMutation()
  const navigate = useNavigate()
  const queryClient = useQueryClient()

  const handleError = (error: any) => {
    Toasts.notify(error.message, { type: 'error' })
  }

  const openArchiveProductModal = async ({
    name,
    id,
    offerType,
    onSuccess,
    onError,
  }: {
    name?: string
    id: string
    offerType?: Offer.Item['offerType']
    onSuccess?: () => void
    onError?: (error: any) => void
  }) => {
    try {
      const offerData = await queryClient.fetchQuery({
        queryKey: ['offer', [id]],
        queryFn: async () => {
          const response = await OffersApiClient.getOffer(id)

          return {
            ...response.data,
            name: decodeHtmlEntities(response.data.name),
          }
        },
      })

      const canProductBeArchived = offerData ? !offerData.parentOffers.length : true

      if (!canProductBeArchived) {
        open(AlertModal, {
          title: `Cannot archive ${name ?? 'this product'}`,
          text: (
            <CannotArchiveProductModalContent
              parentOffers={offerData.parentOffers}
              onClose={close}
            />
          ),
        })

        return
      }
    } catch (error) {}

    const handleSuccess = () => {
      navigate(routing.products.all.self, { replace: true })
      Toasts.notify(`Product${name ? ` ${name}` : ''} archived`, { type: 'info' })
    }

    open(ConfirmModal, {
      title: `Are you sure that you want to archive ${name ?? 'this product'}?`,
      confirmLabel: 'Archive',
      confirmAppearance: 'destructive',
      onConfirm: async () => {
        if (offerType === 'BUNDLE') {
          await archiveOfferBundleMutation.mutateAsync([id], {
            onSuccess: () => {
              handleSuccess()
              onSuccess?.()
            },
            onError: (e) => {
              handleError(e)
              onError?.(e)
            },
          })

          return
        }

        if (offerType === 'COMBINED') {
          await deleteOfferCombinedMutation.mutateAsync([id], {
            onSuccess: () => {
              handleSuccess()
              onSuccess?.()
            },
            onError: (e) => {
              handleError(e)
              onError?.(e)
            },
          })

          return
        }

        await deleteOfferMutation.mutateAsync([{ offerId: id }], {
          onSuccess: () => {
            handleSuccess()
            onSuccess?.()
          },
          onError: (e) => {
            handleError(e)
            onError?.(e)
          },
        })
      },
    })
  }

  return {
    openArchiveProductModal,
    isLoading:
      archiveOfferBundleMutation.isLoading ||
      deleteOfferCombinedMutation.isLoading ||
      deleteOfferMutation.isLoading,
  }
}
